import axios from 'axios';
import {
  DocumentModel,
  DocumentInformationModel,
  BaseDocumentSearchModel,
  ThumbnailViewModel,
  ThumbnailSearchModel,
  DocumentExportOptionsModel,
  AutoSaveConfigurationModel,
  DocumentPagePreferencesModel
} from '@/types';
import AxiosConfiguration from '@/classes/AxiosConfiguration';
import { FindDocumentSearchModel } from '@/types/models/document/FindDocumentSearchModel';

const DocumentApiUrl = 'document/';
const DocumentInformationApiUrl = 'document/information/';
const DocumentExportApiUrl = 'documentExport/';
const MultipleDocumentExportApiUrl = 'documentExport/exportMultipleDocuments/';
const DocumentActionsApiUrl = 'document/Actions/';
const DocumentThumbnailApiUrl = 'document/thumbnail/';
const FindDocumentApiUrl = 'document/findDocument/';
const DocumentPdfApiUrl = 'document/pdf/';
const UpdateDocumentPagePreferenceSettings = 'document/PagePreferences/';

export const DocumentService = {
  async getDocumentAsync(
    cabinetId: string,
    documentId: number,
    page: number,
    viewConversionId: number | null = null,
    autoSaveConfiguration: AutoSaveConfigurationModel | null = null,
    documentPagePreferences: DocumentPagePreferencesModel[] | null = null
  ): Promise<DocumentModel> {
    const response = await AxiosConfiguration.axiosWithoutNotification.get(DocumentApiUrl, {
      params: {
        CabinetId: cabinetId,
        DocumentId: documentId,
        Page: page,
        ViewConversionId: viewConversionId,
        AutoSaveConfiguration: autoSaveConfiguration,
        DocumentPagePreferences: documentPagePreferences
      }
    });
    return response.data;
  },
  async exportDocumentAsync(cabinetId: string, documentId: number, exportOptions: DocumentExportOptionsModel) {
    const response = await AxiosConfiguration.axiosWithoutNotification.put(`${DocumentExportApiUrl}${cabinetId}/${documentId.toString()}`, exportOptions, { responseType: 'blob' });
    return response;
  },
  async exportMultipleDocumentAsZipAsync(cabinetId: string, documentId: number, exportOptions: DocumentExportOptionsModel, fileName: string) {
    const response = await AxiosConfiguration.axiosWithoutNotification.put(`${MultipleDocumentExportApiUrl}${cabinetId}/${documentId.toString()}/${fileName}`, exportOptions, { responseType: 'blob' });
    return response;
  },
  async deleteDocumentAsync(documentSearchModel: BaseDocumentSearchModel): Promise<any> {
    const elements = [documentSearchModel.cabinetId, documentSearchModel.documentId.toString()];

    const url = DocumentApiUrl.concat(elements.join('/'));
    const response = await axios.delete(url);
    return response.data;
  },
  async deletePageAsync(documentSearchModel: BaseDocumentSearchModel, page: number): Promise<any> {
    const elements = [documentSearchModel.cabinetId, documentSearchModel.documentId.toString(), page.toString()];

    const url = DocumentApiUrl.concat(elements.join('/'));
    const response = await axios.delete(url);
    return response.data;
  },
  async getDocumentActionsAsync(documentSearchModel: BaseDocumentSearchModel): Promise<any> {
    const response = await axios.get(DocumentActionsApiUrl, {
      params: {
        CabinetId: documentSearchModel.cabinetId,
        DocumentId: documentSearchModel.documentId,
        DocumentTypeId: documentSearchModel.documentTypeId
      }
    });
    return response.data;
  },
  async getDocumentInformationAsync(cabinetId: string, documentId: number): Promise<DocumentInformationModel> {
    const response = await axios.get(DocumentInformationApiUrl, {
      params: {
        CabinetId: cabinetId,
        DocumentId: documentId
      }
    });
    return response.data;
  },
  async getThumbnailAsync(thumbnailSearchModel: ThumbnailSearchModel): Promise<ThumbnailViewModel> {
    const response = await AxiosConfiguration.axiosWithoutNotification.get(DocumentThumbnailApiUrl, {
      params: {
        CabinetId: thumbnailSearchModel.cabinetId,
        DocumentId: thumbnailSearchModel.documentId,
        DocumentTypeId: thumbnailSearchModel.documentTypeId,
        Page: thumbnailSearchModel.page,
        MaximumWidthInPixels: thumbnailSearchModel.maximumWidthInPixels,
        MaximumHeightInPixels: thumbnailSearchModel.maximumHeightInPixels
      }
    });
    return response.data;
  },
  async findDocumentByReferenceAsync(findDocumentSearchModel: FindDocumentSearchModel): Promise<any> {
    const response = await AxiosConfiguration.axiosWithErrorPage.post(FindDocumentApiUrl, findDocumentSearchModel);
    return response.data;
  },
  async getDocumentAsPdfAsync(cabinetId: string, documentId: number): Promise<string> {
    const response = await axios.get(DocumentPdfApiUrl, { params: { cabinetId, documentId } });
    return response.data;
  },
  async updateDocumentPagePreferenceSettings(documentId: number, documentPagePreference: DocumentPagePreferencesModel): Promise<void> {
    const response = await axios.put(UpdateDocumentPagePreferenceSettings + documentId, documentPagePreference);
    return response.data;
  }
};
